import { useEffect, useRef } from "react";

export const useFaviconPersonality = () => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const updateFavicon = (url: string) => {
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = url;
    };

    const setTemporaryFavicon = (url: string, duration: number = 1500) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      updateFavicon(url);
      timeoutRef.current = setTimeout(() => {
        // Revert to happy or sleepy depending on visibility
        updateFavicon(document.hidden ? "/favicon/sleepy.ico" : "/favicon/happy.ico");
      }, duration);
    };

    // Default state
    updateFavicon(document.hidden ? "/favicon/sleepy.ico" : "/favicon/happy.ico");

    // 1. Visibility changes (sleepy / happy)
    const handleVisibilityChange = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      updateFavicon(document.hidden ? "/favicon/sleepy.ico" : "/favicon/happy.ico");
    };

    // 2. Hover states (exited)
    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInteractive = target.closest("a") || target.closest("button") || target.closest('[role="button"]');
      if (isInteractive && !document.hidden) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        updateFavicon("/favicon/exited.ico");
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInteractive = target.closest("a") || target.closest("button") || target.closest('[role="button"]');
      if (isInteractive && !document.hidden) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        updateFavicon("/favicon/happy.ico");
      }
    };

    // 3. Offline / Error (angry)
    const handleOffline = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      updateFavicon("/favicon/angry.ico");
    };

    const handleOnline = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      updateFavicon(document.hidden ? "/favicon/sleepy.ico" : "/favicon/happy.ico");
    };

    // 4. Theme Toggle (suprised)
    const handleThemeToggle = () => {
      setTemporaryFavicon("/favicon/suprised.ico", 1500);
    };

    // Attach listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.body.addEventListener("mouseover", handleMouseOver);
    document.body.addEventListener("mouseout", handleMouseOut);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    window.addEventListener("theme-toggled", handleThemeToggle);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.body.removeEventListener("mouseover", handleMouseOver);
      document.body.removeEventListener("mouseout", handleMouseOut);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("theme-toggled", handleThemeToggle);
    };
  }, []);
};
